import { DOCS_URLS } from "@p0security/frontend-core";
import type { DockerComponents } from "@p0security/integrations-docker-shared/components";
import {
  VAULT_OUTSIDE_CONNECTOR_CLOUD,
  renderHosting,
} from "@p0security/integrations-docker-shared/hosting";
import type {
  DockerConnectorConfig,
  DockerIntegration,
  DockerOrganizationConfig,
} from "@p0security/integrations-docker-shared/types";
import {
  type FrontendInstallContext,
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import { p0InvokerEmail } from "@p0security/integrations-vaulted-credential-shared";
import {
  installedItem,
  installedItems,
} from "@p0security/shared/install/installed";
import {
  Alert,
  Collapse,
  CommandDisplay,
  Flex,
  Spacing,
  Typography,
} from "@p0security/ui";
import { assertNever } from "@p0security/util/assertNever";
import { compact, sortBy } from "lodash-es";
import type { ReactNode } from "react";

import { renderConnectorTerraform } from "./connector-iac.ts";
import {
  type ReadGrantBlock,
  isVaultAccountBlock,
  renderReadGrantTerraform,
} from "./organization-iac.ts";
import { ConnectorHelp, OrganizationSteps, type ReadGrant } from "./steps.tsx";

const { Link, Paragraph } = Typography;

type DockerInstaller = FrontendInstaller<typeof DockerComponents>;
type InstallContext = FrontendInstallContext<DockerIntegration>;

/** Creates the provider that lists P0's installs of `integration`'s
 * iam-write component: the accounts, or projects, that a field placing a
 * connector or a secret can name. */
const newIamWriteInstallsProvider = (integration: string) =>
  otherInstalledIntegration({ component: "iam-write", integration });

/** The connectors an organization can name: this integration's own installed
 * connectors, each with where it runs.
 *
 * Read from the installer's live config rather than fetched: the pointer
 * targets a sibling component, not another integration, so
 * `otherInstalledIntegration` would spend a read on a document the install
 * context already holds.
 */
const listConnectors = async ({ config }: InstallContext) =>
  sortBy(
    compact(
      Object.entries(config ? installedItems("connector", config) : {}).map(
        ([id, connector]) =>
          connector && {
            id,
            label: `${id}: ${renderHosting(connector.hosting)}`,
          }
      )
    ),
    "label"
  );

/** Whether a stored item has been set up: its page then shows the form, and
 * its Terraform only on request. */
const isSetUp = (item: { state?: string } | undefined) =>
  item?.state === "configure" || item?.state === "installed";

const DocsLink: React.FC = () => (
  <Link
    href={DOCS_URLS.DOCKER_INSTALL}
    rel="noopener noreferrer"
    target="_blank"
  >
    Docker Hub install guide
  </Link>
);

/** Why a connector on Cloud Run can't be set up yet: its Terraform lets in
 * the service account P0 invokes it as, which is the one P0's Google Cloud
 * integration has. */
const MISSING_GOOGLE_CLOUD =
  "P0 invokes a connector on Cloud Run as the service account of its Google Cloud integration, which isn't installed. Install P0's Google Cloud IAM management for the connector's project first.";

/** Says why a connector on Cloud Run has no Terraform to show. */
const MissingGoogleCloud: React.FC = () => (
  <Alert message={MISSING_GOOGLE_CLOUD} showIcon type="error" />
);

/** Shown above the new connector's form. */
const NewConnectorPreamble: React.FC = () => (
  <>
    <Paragraph>
      P0 grants roles in your Docker Hub organizations through a connector that
      you deploy in your own AWS account or Google Cloud project. Only the
      connector reads an organization&apos;s access token, and it acts only for
      the email domains you allow on its deployment.
    </Paragraph>
    <Paragraph>
      One connector can serve many organizations, so one is usually enough. Have
      ready P0&apos;s AWS IAM management or Google Cloud IAM management
      integration for the account or project the connector will run in. Where it
      runs can&apos;t change once the connector is added. See the <DocsLink />.
    </Paragraph>
  </>
);

/** Shown above the new organization's form. */
const NewOrganizationPreamble: React.FC = () => (
  <>
    <Paragraph>
      Add each Docker Hub organization that P0 grants roles in. An organization
      names the connector that acts on it, and the secret, in your own secret
      manager, that holds its organization access token. Organization access
      tokens need a Docker Team or Business plan.
    </Paragraph>
    <Paragraph>
      Install a connector first. {VAULT_OUTSIDE_CONNECTOR_CLOUD} It can be in
      another account or project. Have ready P0&apos;s AWS Secrets Manager or
      Google Secret Manager integration for where the secret is. None of this
      can change once the organization is added. The next page shows how to
      create the token, store it and let the connector read it. See the{" "}
      <DocsLink />.
    </Paragraph>
  </>
);

/** What to do with one configuration of the organization's read grant, shown
 * above it. `last` is whether "Next" follows it. */
const toGrantHeader = (block: ReadGrantBlock, { last }: { last: boolean }) => {
  const next = last ? <> Then click &quot;Next&quot;.</> : null;
  switch (block.purpose) {
    case "grant":
      return (
        <Paragraph>
          Add the following to your Terraform and apply it. It lets the
          connector read this organization&apos;s access token.{next}
        </Paragraph>
      );
    case "vault-account":
      return (
        <>
          <Paragraph>
            Then add the following where you manage AWS account{" "}
            {block.accountId}, which holds the secret, and apply it. Add its
            output to the policy of the secret&apos;s KMS key.{next}
          </Paragraph>
          <Spacing mb={12}>
            <Alert
              message="This replaces the secret's resource policy. If the secret already has one, add the statement to that policy instead."
              showIcon
              type="warning"
            />
          </Spacing>
        </>
      );
    default:
      throw assertNever(block);
  }
};

/** What a set-up organization's page calls each configuration of its read
 * grant. */
const GRANT_REFERENCE_LABELS: Record<ReadGrantBlock["purpose"], string> = {
  grant: "Show the Terraform that lets the connector read the token",
  "vault-account": "Show the Terraform for the secret's account",
};

/** A set-up item's Terraform, collapsed, for an admin who needs it again. */
const TerraformReference: React.FC<{
  blocks: { key: string; label: string; terraform: string }[];
  children?: ReactNode;
}> = ({ blocks, children }) => (
  <Flex gap={8} vertical>
    {children}
    <Collapse
      items={blocks.map(({ key, label, terraform }) => ({
        children: <CommandDisplay commands={terraform} maxRows={20} />,
        key,
        label,
      }))}
    />
  </Flex>
);

/** Renders an organization's read-grant Terraform from what the item and its
 * connector store, or says why it can't be. */
const toReadGrant = (
  context: InstallContext,
  org: string,
  item: DockerOrganizationConfig
): ReadGrant => {
  const hosting = context.config
    ? installedItem("connector", context.config, item.connector)?.hosting
    : undefined;
  if (!hosting) return { status: "no-connector" };
  const blocks = renderReadGrantTerraform({
    connectorId: item.connector,
    hosting,
    org,
    organization: item,
  });
  if (!blocks) return { hosting, status: "vault-outside-cloud" };
  return {
    blocks,
    hosting,
    status: "ready",
    vaultAccountId: blocks.find(isVaultAccountBlock)?.accountId,
  };
};

/** The configurations of a read grant, which has none until it's ready. */
const toBlocks = (grant: ReadGrant): ReadGrantBlock[] => {
  switch (grant.status) {
    case "no-connector":
    case "vault-outside-cloud":
      return [];
    case "ready":
      return grant.blocks;
    default:
      throw assertNever(grant);
  }
};

const renderConnectorInstructions = (
  context: InstallContext,
  connectorId: string,
  { hosting }: DockerConnectorConfig
) => renderConnectorTerraform({ connectorId, context, hosting });

/**
 * Installs the Docker Hub integration.
 *
 * A connector comes first: the new item's form takes its ID and where it
 * runs, and its page shows the Terraform that deploys it above the "Next"
 * that checks P0 can invoke it, and "Finish" on the next page installs it.
 * Each organization then names a connector and the secret that holds its
 * access token, and its page shows the steps to create the token, store it
 * and let the connector read it, above the "Next" that validates the install
 * through the connector. "Finish" on the next page installs it.
 *
 * Once an item is set up, its page keeps its Terraform, collapsed.
 */
export const dockerInstaller: DockerInstaller = {
  components: {
    connector: {
      instructions: (context, id, item) => {
        const terraform = renderConnectorInstructions(context, id, item);
        return terraform === undefined
          ? { help: <MissingGoogleCloud /> }
          : {
              commands: { iac: [{ command: terraform }] },
              help: (
                <ConnectorHelp
                  adminEmail={context.user?.email}
                  connectorId={id}
                  hosting={item.hosting}
                />
              ),
            };
      },
      items: {
        hosting: {
          labeler: () =>
            "Where the connector runs. Its Terraform deploys it there, and P0 invokes it there.",
          options: {
            aws: {
              items: {
                accountId: {
                  labeler: () =>
                    "The AWS account the connector runs in. P0 lists the accounts where its AWS IAM management is installed.",
                  optionProvider: newIamWriteInstallsProvider("aws"),
                },
                connectorName: {
                  labeler: () =>
                    "The name of the connector's Lambda function. Its Terraform creates the function, and an IAM role of the same name that the connector runs as.",
                },
                connectorRegion: {
                  labeler: () =>
                    "The AWS region the connector runs in, such as us-west-2.",
                },
              },
            },
            gcp: {
              items: {
                connectorName: {
                  labeler: () =>
                    "The name of the connector's Cloud Run service, in 6 to 30 characters. Its Terraform creates the service, and a service account of the same name that the connector runs as.",
                },
                connectorRegion: {
                  labeler: () =>
                    "The Google Cloud region the connector runs in, such as us-west1.",
                },
                projectId: {
                  labeler: () =>
                    "The Google Cloud project the connector runs in. P0 lists the projects where its Google Cloud IAM management is installed.",
                  optionProvider: newIamWriteInstallsProvider("gcloud"),
                },
              },
              prerequisiteMessages: async (context) =>
                p0InvokerEmail(context.installed)
                  ? undefined
                  : MISSING_GOOGLE_CLOUD,
            },
          },
        },
      },
      labeler: () =>
        "A name for this connector in P0, such as prod. Each organization you add picks its connector by this name.",
      newPreamble: () => <NewConnectorPreamble />,
      // The framework passes the configure form's draft, so this reads the
      // item P0 stored.
      preamble: (context, id) => {
        const stored = context.config?.connector?.[id];
        if (!stored || !isSetUp(stored)) return undefined;
        const terraform = renderConnectorInstructions(context, id, stored);
        return terraform === undefined ? (
          <MissingGoogleCloud />
        ) : (
          <TerraformReference
            blocks={[
              {
                key: "terraform",
                label: "Show the connector's Terraform",
                terraform,
              },
            ]}
          />
        );
      },
    },
    organization: {
      // The item was saved with every field the Terraform needs, so it
      // renders from what the item and its connector store.
      instructions: (context, org, item) => {
        const grant = toReadGrant(context, org, item);
        const blocks = toBlocks(grant);
        return {
          commands:
            blocks.length > 0
              ? {
                  iac: blocks.map((block, index) => ({
                    command: block.terraform,
                    header: toGrantHeader(block, {
                      last: index === blocks.length - 1,
                    }),
                  })),
                }
              : undefined,
          help: (
            <OrganizationSteps
              adminEmail={context.user?.email}
              grant={grant}
              item={item}
              org={org}
            />
          ),
        };
      },
      items: {
        connector: {
          optionProvider: listConnectors,
          prerequisiteMessages: async (context) =>
            (await listConnectors(context)).length === 0
              ? "Install a Docker Hub connector first."
              : undefined,
        },
        vault: {
          labeler: () =>
            "Where the organization access token is stored. It must be in the connector's cloud, and can be in another account or project.",
          options: {
            "aws-sm": {
              items: {
                install: {
                  labeler: () =>
                    "The AWS account the secret is in. P0 lists the accounts where its AWS Secrets Manager integration is installed.",
                  optionProvider: newIamWriteInstallsProvider("aws-sm"),
                },
                secretsRegion: {
                  labeler: () =>
                    "The AWS region the secret is in, such as us-west-2.",
                },
              },
            },
            "gcp-sm": {
              items: {
                install: {
                  labeler: () =>
                    "The Google Cloud project the secret is in. P0 lists the projects where its Google Secret Manager integration is installed.",
                  optionProvider: newIamWriteInstallsProvider("gcp-sm"),
                },
              },
            },
          },
        },
      },
      labeler: () =>
        "The organization's name on Docker Hub, as in its repositories' names: <organization>/<repository>.",
      newPreamble: () => <NewOrganizationPreamble />,
      // As the connector's: the stored item, which also has the allowed
      // domains the last validation read.
      preamble: (context, org) => {
        const stored = context.config?.organization?.[org];
        if (!stored || !isSetUp(stored)) return undefined;
        const grant = toReadGrant(context, org, stored);
        return (
          <TerraformReference
            blocks={toBlocks(grant).map(({ purpose, terraform }) => ({
              key: purpose,
              label: GRANT_REFERENCE_LABELS[purpose],
              terraform,
            }))}
          >
            <OrganizationSteps
              adminEmail={context.user?.email}
              grant={grant}
              item={stored}
              org={org}
            />
          </TerraformReference>
        );
      },
    },
  },
};
